import React, { Children, memo, cloneElement, ReactElement } from 'react'
import { OnDragEndResponder, DragDropContext } from 'react-beautiful-dnd'
import classNames from 'classnames'
import { TableBody } from './TableBody'
import 'assets/scss/4_base/_tables_new.scss'

export type DragAction = { dragIdx: number; dropIdx: number }

export type IStickyHeaderStyleProps = {
  top?: string
  'z-index'?: number
}

export interface ITableProps {
  id?: string
  children: ReactElement[]
  dataTest?: string
  isDraggable?: boolean
  onUpdateRowOrder?: (rowUpdate: DragAction) => void
  className?: string
  fixed?: boolean
  /**
   * Allows the headers on the table to stick to the top while scrolling.
   *
   * @default false
   */
  stickyHeader?: boolean
  /**
   * Adds additional styles to the sticky header, useful if you are adding an action bar above thats also sticky.
   * Use this to add additional offset or if you need to configure z-indexes.
   */
  stickyHeaderStyle?: IStickyHeaderStyleProps
}

const NOOP = () => {}

export const Table = memo<ITableProps>(
  ({
    id,
    children,
    dataTest,
    isDraggable = false,
    onUpdateRowOrder = NOOP,
    fixed,
    stickyHeader = false,
    stickyHeaderStyle,
    className,
  }) => {
    const tableClassNames = classNames(
      'table',
      { fixed },
      { 'sticky-header': stickyHeader },
      className
    )
    const sticky = { stickyHeader, stickyHeaderStyle }
    if (isDraggable) {
      const onDragEnd: OnDragEndResponder = ({ destination, source }) => {
        if (!destination || source.index === destination.index) {
          return
        }
        onUpdateRowOrder({ dragIdx: source.index, dropIdx: destination.index })
      }

      return (
        <DragDropContext onDragEnd={onDragEnd}>
          <table className={tableClassNames} id={id} data-test={dataTest}>
            {Children.map(children, (child: ReactElement) => {
              const passedProps = child.type === TableBody ? { isDraggable, ...sticky } : sticky
              return cloneElement(child, passedProps)
            })}
          </table>
        </DragDropContext>
      )
    }
    return (
      <table className={tableClassNames} id={id} data-test={dataTest}>
        {Children.map(children, (child: ReactElement) => {
          return cloneElement(child, sticky)
        })}
      </table>
    )
  }
)

export default Table
